/**
 * DSPy artifact operator client.
 *
 * Mirrors backend /api/emails/ai/dspy-artifacts/ (Phase 3):
 *   GET  → list compiled email-classifier artifacts (metadata + eval).
 *   POST → {action: 'promote'|'rollback', artifact_id, reason?}.
 *
 * The nightly compile writes draft artifacts; this lets an operator see
 * them and promote the best one to 'active' (or roll the active one back).
 * Best-effort: a 404 (backend without the endpoint) leaves the card hidden.
 */

import { useCallback, useEffect, useState } from 'react'
import api from '../hooks/useApi'

export interface DspyArtifact {
  id: string
  domain: string
  scope: string
  organisation_id: number | null
  version_number: number
  status: string
  is_active: boolean
  eval_score: number | null
  eval_metrics: Record<string, unknown>
  created_by_type: string
  created_at: string | null
  activated_at: string | null
  rolled_back_at: string | null
  rollback_reason: string
}

export interface DspyArtifactList {
  artifacts: DspyArtifact[]
  count: number
  dspy_runtime_enabled: boolean
}

interface UseDspyArtifactsResult {
  data: DspyArtifactList | null
  loading: boolean
  error: string | null
  /** True when the backend has the endpoint (controls whether to render). */
  available: boolean
  refresh: () => Promise<void>
  promote: (id: string) => Promise<boolean>
  rollback: (id: string, reason?: string) => Promise<boolean>
}

// NOTE: the shared axios client already carries baseURL `/api`; a leading
// `/api/` here produced the prod `/api/api/...` 404s.
const ENDPOINT = '/emails/ai/dspy-artifacts/'

export function useDspyArtifacts(enabled = true): UseDspyArtifactsResult {
  const [data, setData] = useState<DspyArtifactList | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [available, setAvailable] = useState(true)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: payload } = await api.get<DspyArtifactList>(ENDPOINT)
      setData(payload ?? null)
      setAvailable(true)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        setAvailable(false)
        setData(null)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load artifacts')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const act = useCallback(
    async (action: 'promote' | 'rollback', id: string, reason?: string) => {
      try {
        const { data: result } = await api.post<{ ok?: boolean; error?: string }>(
          ENDPOINT,
          { action, artifact_id: id, reason },
        )
        if (!result?.ok) {
          setError(result?.error || `${action} failed`)
          return false
        }
        await refresh()
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : `${action} failed`)
        return false
      }
    },
    [refresh],
  )

  const promote = useCallback((id: string) => act('promote', id), [act])
  const rollback = useCallback((id: string, reason?: string) => act('rollback', id, reason), [act])

  useEffect(() => {
    if (enabled) void refresh()
  }, [enabled, refresh])

  return { data, loading, error, available, refresh, promote, rollback }
}
